import React, { useContext, useEffect, useMemo, useRef } from 'react';
import _ from 'lodash';

import DateInputContext from 'contexts/DateInputContext';
import DateInputProvider from 'contextProviders/DateInputProvider';

// Blocks and Composite elements can override the canvas date with their own
// (the DateFiltered shape dashboard gadgets persist). These helpers keep the
// shape normalised, so saved and live values compare with _.isEqual.

export const pickDateSettings = ({
  dateField,
  dateRange,
  relativeDateRange,
  advancedRelativeDateRange,
}: DateFiltered): DateFiltered => ({
  dateField,
  dateRange,
  relativeDateRange,
  advancedRelativeDateRange,
});

export const isSameDateSettings = ({
  a,
  b,
}: {
  a: DateFiltered | undefined;
  b: DateFiltered | undefined;
}): boolean => _.isEqual(a && pickDateSettings(a), b && pickDateSettings(b));

// The date settings of the nearest DateInputProvider
export const useCurrentDateSettings = (): DateFiltered => {
  const { dateField, dateRange, relativeDateRange, advancedRelativeDateRange } =
    useContext(DateInputContext);
  return useMemo(
    () =>
      pickDateSettings({
        dateField,
        dateRange,
        relativeDateRange,
        advancedRelativeDateRange,
      }),
    [advancedRelativeDateRange, dateField, dateRange, relativeDateRange],
  );
};

// DateInputProvider only reads its initial values on mount, so later changes
// are pushed into it through its setters (skipping ones that already match,
// which would otherwise re-render for nothing)
export const DateSettingsSync = ({
  dateSettings,
}: {
  dateSettings: DateFiltered;
}) => {
  const {
    dateField,
    dateRange,
    relativeDateRange,
    advancedRelativeDateRange,
    setDateField,
    setDateRange,
    setRelativeDateRange,
    setAdvancedRelativeDateRange,
  } = useContext(DateInputContext);
  const current = {
    dateField,
    dateRange,
    relativeDateRange,
    advancedRelativeDateRange,
  };
  const currentRef = useRef(current);
  currentRef.current = current;

  useEffect(() => {
    const now = currentRef.current;
    if (now.dateField !== dateSettings.dateField) {
      setDateField(dateSettings.dateField);
    }
    if (!_.isEqual(now.dateRange, dateSettings.dateRange)) {
      setDateRange(dateSettings.dateRange);
    }
    if (!_.isEqual(now.relativeDateRange, dateSettings.relativeDateRange)) {
      setRelativeDateRange(dateSettings.relativeDateRange);
    }
    if (
      !_.isEqual(
        now.advancedRelativeDateRange,
        dateSettings.advancedRelativeDateRange,
      )
    ) {
      setAdvancedRelativeDateRange(dateSettings.advancedRelativeDateRange);
    }
  }, [
    dateSettings,
    setAdvancedRelativeDateRange,
    setDateField,
    setDateRange,
    setRelativeDateRange,
  ]);

  return null;
};

// Whether the settings set a range, not just a date field (report gadgets
// only choose their field; the report sets the range)
export const hasDateRange = (dateSettings: DateFiltered): boolean =>
  !!(
    dateSettings.dateRange ||
    dateSettings.relativeDateRange ||
    dateSettings.advancedRelativeDateRange
  );

// The dates something follows: its own, or else the inherited ones. Its own
// date field alone keeps the inherited range.
export const inheritDateSettings = ({
  own,
  inherited,
}: {
  own: DateFiltered | undefined;
  inherited: DateFiltered;
}): DateFiltered => {
  if (!own) {
    return inherited;
  }
  return hasDateRange(own)
    ? own
    : { ...pickDateSettings(inherited), dateField: own.dateField };
};

const OwnDatesScope = ({
  dateSettings,
  children,
}: {
  dateSettings: DateFiltered;
  children: JSX.Element;
}) => (
  <DateInputProvider
    initialDateField={dateSettings.dateField}
    initialDateRange={dateSettings.dateRange}
    initialRelativeDateRange={dateSettings.relativeDateRange}
    initialAdvancedRelativeDateRange={dateSettings.advancedRelativeDateRange}
  >
    <DateSettingsSync dateSettings={dateSettings} />
    {children}
  </DateInputProvider>
);

// Its own date field over the range around it, following that range
const OwnDateFieldScope = ({
  dateField,
  children,
}: {
  dateField: string;
  children: JSX.Element;
}) => {
  const around = useCurrentDateSettings();
  const dateSettings = useMemo(
    () => ({ ...around, dateField }),
    [around, dateField],
  );
  return <OwnDatesScope dateSettings={dateSettings}>{children}</OwnDatesScope>;
};

// Scopes a block's (or element's) queries to its own dates when it has them;
// otherwise it follows the dates around it. Only a date field of its own
// keeps the range around it.
export const DateScope = ({
  dateSettings,
  children,
}: {
  dateSettings: DateFiltered | undefined;
  children: JSX.Element;
}) => {
  if (!dateSettings) {
    return children;
  }
  return hasDateRange(dateSettings) ? (
    <OwnDatesScope dateSettings={dateSettings}>{children}</OwnDatesScope>
  ) : (
    <OwnDateFieldScope dateField={dateSettings.dateField}>
      {children}
    </OwnDateFieldScope>
  );
};
